import type {
  MatchupPair,
  MatchupsResponse,
  UpcomingWeek,
} from '../../shared/types';
import { startingSlots, winProbability, type MarginFit } from './projections';

/**
 * Pre-kickoff matchups: live lineups joined to build-time projections.
 *
 * Imports no React, like every other module in this directory, so the esbuild
 * harness can compile and exercise it without a browser.
 *
 * WHY THE TWO HALVES COME FROM DIFFERENT PLACES. A week of Sleeper projections
 * is a 2 MB download whose JSON.parse alone costs ~13 ms -- past the Workers
 * free tier's 10 ms CPU budget for a single invocation -- so it has to be
 * fetched at build time and committed (see scripts/sync-projections.mjs). The
 * starting lineups, by contrast, are already served live and cheaply by
 * /api/matchups, and they change the moment a manager taps a player. Freezing
 * them into the same nightly snapshot would mean a lineup edit went unseen for
 * up to a day. So: projections committed, lineups live, joined here on
 * `player_id`.
 */

/** One starter, with what the projections expect of them. */
export interface PreviewPlayer {
  playerId: string;
  /** The slot they occupy, e.g. "FLEX" -- from the era-correct template. */
  slot: string;
  /**
   * Null when the feed has no row for this player, which is NOT zero. Byes, IR
   * and deep stashes simply are not projected, and rendering that as 0.00 would
   * claim they are expected to score nothing.
   */
  projected: number | null;
}

export interface PreviewSide {
  rosterId: number;
  starters: PreviewPlayer[];
  /**
   * Sum of the projections we have. A FLOOR rather than an estimate whenever
   * `unprojected` is above zero, because the missing players are unknown and
   * not zero -- which is why the UI qualifies the number in that case.
   */
  projected: number;
  /** Starting slots with a player in them but no projection to show. */
  unprojected: number;
  /** Empty starting slots -- the string "0" upstream, not null. */
  empty: number;
  /**
   * Probability this side wins, under the margin model fitted from the league's
   * own completed games.
   *
   * Deliberately not "whoever projects higher wins": every roster in this league
   * projects into a narrow band, so a naive reading of a two-point edge as a
   * certainty is wildly overconfident -- it once produced a 6-10 manager with
   * one expected win. See `fitMargins` for the measurement behind that.
   *
   * Null when there is no opponent, and ALSO null when either lineup contains a
   * starter with no projection. In that case the projected total is a floor
   * rather than an estimate, so a margin computed from it is biased by an
   * unknown amount -- and always against the side with the gap. Left unset
   * rather than shown with a caveat, because the failure is asymmetric and
   * quantifying it would mean inventing the missing player's score.
   */
  winProbability: number | null;
}

export interface PreviewPair {
  matchupId: number;
  sides: PreviewSide[];
  /** Projected margin between the two sides. Null unless there are exactly two. */
  margin: number | null;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Whether this file's projections describe the week being viewed.
 *
 * Both halves of the key matter. The season guards against showing 2026's
 * projections over an archived 2025 week -- roster ids repeat across seasons, so
 * the join would succeed and produce plausible nonsense. The week guards against
 * showing week 1's projections on week 5, which is a different set of games
 * entirely.
 */
export function coversWeek(
  upcoming: UpcomingWeek | null | undefined,
  matchups: MatchupsResponse,
): boolean {
  if (!upcoming) return false;
  return upcoming.season === matchups.season && upcoming.week === matchups.week;
}

function toSide(
  pair: MatchupPair,
  index: number,
  slots: string[],
  projected: Record<string, number>,
): PreviewSide {
  const side = pair.teams[index];

  let total = 0;
  let unprojected = 0;
  let empty = 0;

  const starters: PreviewPlayer[] = side.starters.map((s, i) => {
    // `starters` is positionally aligned with the non-bench entries of
    // roster_positions; that alignment is the only thing that distinguishes a
    // FLEX slot from an RB one. An unfilled slot is the string "0".
    const slot = slots[i] ?? '?';
    if (!s.playerId || s.playerId === '0') {
      empty += 1;
      return { playerId: s.playerId, slot, projected: null };
    }

    // Absent from the map means unknown, so read it as a miss rather than a 0.
    const proj = Object.prototype.hasOwnProperty.call(projected, s.playerId)
      ? projected[s.playerId]
      : null;
    if (proj === null) unprojected += 1;
    else total += proj;

    return { playerId: s.playerId, slot, projected: proj };
  });

  return {
    rosterId: side.rosterId,
    starters,
    projected: round2(total),
    unprojected,
    empty,
    winProbability: null, // filled in below, once both totals are known
  };
}

/**
 * Every matchup in the week, as projections rather than results.
 *
 * Returns an empty list when the projections do not cover this week, so a caller
 * can treat "no preview" and "nothing to preview" identically.
 */
export function buildPreview(
  matchups: MatchupsResponse,
  upcoming: UpcomingWeek | null | undefined,
  fit: MarginFit,
): PreviewPair[] {
  if (!coversWeek(upcoming, matchups)) return [];

  // Prefer the template that came back with the matchups: it is the league's
  // own, for the exact season being viewed. The projections file carries one too
  // and they agree, but only the response's is guaranteed era-correct if a
  // future season's week is ever rendered against an older snapshot.
  const slots = startingSlots(
    matchups.rosterPositions.length > 0
      ? matchups.rosterPositions
      : (upcoming as UpcomingWeek).rosterPositions,
  );
  const projected = (upcoming as UpcomingWeek).projected;

  return matchups.matchups.map((pair) => {
    const sides = pair.teams.map((_, i) => toSide(pair, i, slots, projected));

    // Only when both totals are complete -- see `winProbability` above for why a
    // floor cannot be fed to the margin model.
    if (sides.length === 2 && sides.every((s) => s.unprojected === 0)) {
      const [a, b] = sides;
      a.winProbability = winProbability(a.projected - b.projected, fit);
      b.winProbability = winProbability(b.projected - a.projected, fit);
    }

    return {
      matchupId: pair.matchupId,
      sides,
      margin: sides.length === 2 ? round2(Math.abs(sides[0].projected - sides[1].projected)) : null,
    };
  });
}

/**
 * League-wide facts about a previewed week.
 *
 * No longer rendered anywhere: it was the /scoreboard page's dateline, and the
 * home page's matchup section is one section of a page about the season, so a
 * week's projected spread has no dateline to sit in. Kept because
 * `verify:projections` asserts over it and prints the range for eyeballing,
 * which is the check that would notice the whole week collapsing to one number.
 */
export interface PreviewSummary {
  teams: number;
  /** Highest projected total in the week. */
  high: number;
  /** Lowest projected total in the week. */
  low: number;
  /** Starting slots across the league with a player but no projection. */
  unprojected: number;
  /** Empty starting slots across the league -- a lineup nobody has finished. */
  empty: number;
}

export function summarisePreview(pairs: PreviewPair[]): PreviewSummary | null {
  const sides = pairs.flatMap((p) => p.sides);
  if (sides.length === 0) return null;

  const totals = sides.map((s) => s.projected);
  return {
    teams: sides.length,
    high: Math.max(...totals),
    low: Math.min(...totals),
    unprojected: sides.reduce((t, s) => t + s.unprojected, 0),
    empty: sides.reduce((t, s) => t + s.empty, 0),
  };
}
